import { useCallback, useEffect, useRef, useState } from "react";
import { useMutation, useQuery } from "@tanstack/react-query";
import { marketingEventBus } from "@platform/events/marketing.events";
import {
  useIntakeQuizTracking,
  useMarketingTracking,
} from "../../marketing.analytics";
import { submitInquiry } from "../../utils/submitInquiry";
import { intakeFormQuery, type IntakeFormConfig } from "../IntakeForm";
import {
  MarketingNewsletterForm,
  type NewsletterConfirmation,
} from "./MarketingNewsletterForm";
import type { NewsletterFormData } from "./newsletter.schema";

const FORM_SLUG = "newsletter";

/** The anchor the form sits at on the homepage. */
const NEWSLETTER_ANCHOR = "newsletter";

export type Props = {
  className?: string;
};

/**
 * Container for MarketingNewsletterForm.
 *
 * Sends the signup to intake, where it is recorded with every other inquiry
 * and passed on to the mailing list.
 */
export function MarketingNewsletterFormContainer({ className }: Props) {
  const { trackCtaClick } = useMarketingTracking();

  // The signup is an intake form, and filling it in is measured as every
  // intake form is, as the answering of its quiz. The form's configuration is
  // read only for the ids those measurements carry.
  const { data: config } = useQuery<IntakeFormConfig>(intakeFormQuery(FORM_SLUG));
  const track = useIntakeQuizTracking({
    quizId: config?.quiz_id ?? undefined,
    formId: config?.id,
    formSlug: FORM_SLUG,
    // Set into the homepage at this anchor, so arriving at it by a link is
    // told apart from scrolling down to it.
    anchor: NEWSLETTER_ANCHOR,
  });

  // Seeing the form is reported once its ids are known, whichever comes
  // second, so that the event is not sent without them. Typing into it is
  // held to the same, and to one more thing: nobody starts a form they were
  // not shown, so starting reports the seeing first if that has not gone.
  // Somebody quick, or a browser that fills the fields in, would otherwise
  // be recorded as starting a form before being shown it.
  const [isSeen, setIsSeen] = useState(false);
  const [isStarted, setIsStarted] = useState(false);
  const reported = useRef(false);
  const reportedStart = useRef(false);
  useEffect(() => {
    if (!config) return;
    if ((isSeen || isStarted) && !reported.current) {
      reported.current = true;
      track.loaded();
    }
    if (isStarted && !reportedStart.current) {
      reportedStart.current = true;
      track.started();
    }
  }, [isSeen, isStarted, config, track]);
  const handleSeen = useCallback(() => setIsSeen(true), []);
  const handleStart = useCallback(() => setIsStarted(true), []);

  const [confirmation, setConfirmation] = useState<NewsletterConfirmation>();

  const subscribe = useMutation({
    mutationFn: async (data: NewsletterFormData) => {
      marketingEventBus.emitEvent("newsletter:submitted", data);
      trackCtaClick("newsletter_subscribe");

      const response:
        | {
            quiz_response_id?: number | null;
            confirmation_heading?: string | null;
            confirmation_body?: string | null;
          }
        | undefined = await submitInquiry(
          FORM_SLUG,
          {
            email: data.email,
            firstName: data.firstName,
            lastName: data.lastName,
          },
          "newsletter:page"
        );
      track.completed(response?.quiz_response_id);
      // What the page says afterwards is the form's own wording, set in
      // admin, and it comes back with the submission.
      if (response?.confirmation_heading || response?.confirmation_body) {
        setConfirmation({
          heading: response.confirmation_heading ?? "",
          body: response.confirmation_body ?? "",
        });
      }
    },
    onSuccess: (_data, variables) => {
      marketingEventBus.emitEvent("newsletter:subscribed", variables);
    },
    onError: (error) => {
      track.failed(error instanceof Error ? error.message : "unknown");
    },
  });

  return (
    <MarketingNewsletterForm
      onSubmit={subscribe.mutateAsync}
      onSeen={handleSeen}
      onStart={handleStart}
      confirmation={confirmation}
      className={className}
    />
  );
}

export default MarketingNewsletterFormContainer;
